import {
  type CreateReference,
  createExtensionValue,
  createFullHumanName,
  ExtensionUrl,
  findExtensionValue,
  getIdentifier,
  IdentifierCode,
  type RequiredCodeableConcept,
  updateExtensions,
  valsOrNone,
} from '@quromedical/fhir-common';
import { compact, first } from '@quromedical/utils';
import { isRequiredCoding } from '../admission/extension';
import { attachIdentifiers, createOptionalIdentifier } from '../mappings';
import {
  mapFhirPersonToContactModel,
  mapFhirPersonToGeneralModel,
  mapPersonContactToFhirPartial,
} from '../person/mapping';
import type { PersonContact } from '../person/schema';
import type { WithId } from '../types';

import type {
  External,
  PartialPractitioner,
  PractitionerCreateResponse,
  PractitionerGeneral,
  PractitionerGetOneResponse,
  PractitionerQualificationCode,
  QualificationFields,
  Settings,
} from './schema';

const isRequiredPractitionerQualification = (
  coding: fhir4.PractitionerQualification,
): coding is PractitionerQualificationCode =>
  isRequiredCoding(first(coding.code.coding));

const createCanPrescribeExtension = createExtensionValue(
  ExtensionUrl.medicationRequestPrescriber,
);

const isPrescriber = findExtensionValue(
  ExtensionUrl.medicationRequestPrescriber,
);

export const mapFhirToQualificationModel = (
  fhir: fhir4.Practitioner,
): Partial<QualificationFields> => {
  const registrationNumber = getIdentifier(
    IdentifierCode.registrationNumber,
    fhir.identifier,
  );
  const qualificationCode = fhir.qualification?.find(
    isRequiredPractitionerQualification,
  );

  return { registrationNumber, qualificationCode };
};

export const mapFhirToExternalModel = (
  fhir: fhir4.Practitioner,
): Partial<External> => {
  const ampathMnemonic = getIdentifier(
    IdentifierCode.ampathMnemonic,
    fhir.identifier,
  );

  return { ampathMnemonic };
};

export const mapFhirToSettingsModel = (
  fhir: fhir4.Practitioner,
): Partial<Settings> => {
  const canPrescribe = isPrescriber(fhir.extension);

  return { canPrescribe };
};

export const mapPractitionerGeneral = (
  fhir: fhir4.Practitioner,
): Partial<PractitionerGeneral> => {
  const generalModel = mapFhirPersonToGeneralModel(fhir);
  const { familyName, givenName, gender, prefixName } = generalModel;
  return { familyName, givenName, gender, prefixName };
};

export const mapFhirToModelPractitioner = (
  fhir: fhir4.Practitioner,
): PartialPractitioner => {
  const contact = mapFhirPersonToContactModel(fhir);
  const general = mapPractitionerGeneral(fhir);
  const qualification = mapFhirToQualificationModel(fhir);
  const active = fhir.active || false;
  const external = mapFhirToExternalModel(fhir);
  const settings = mapFhirToSettingsModel(fhir);
  return {
    qualification,
    contact,
    general,
    active,
    external,
    settings,
  };
};

export const createPractitionerQualification = (
  qualification: RequiredCodeableConcept,
): PractitionerQualificationCode => ({ code: qualification });

const getQualificationCode = (
  qualification: Partial<QualificationFields>,
): PractitionerQualificationCode[] | undefined =>
  valsOrNone(compact([qualification.qualificationCode]));

const fhirGeneral = (general: Partial<PractitionerGeneral>) => {
  const name = createFullHumanName({
    given: general.givenName,
    family: general.familyName,
    prefix: general.prefixName,
    use: 'official',
  });
  const { gender } = general;

  return {
    name,
    gender,
  };
};

export const mapModelToFhirPractitioner = (
  practitioner: PartialPractitioner,
): fhir4.Practitioner => {
  const {
    contact,
    general,
    qualification,
    active,
    settings = {},
  } = practitioner;
  const { canPrescribe } = settings;

  const qualificationCode = getQualificationCode(qualification);

  const extensionList = [
    canPrescribe !== undefined
      ? createCanPrescribeExtension(canPrescribe)
      : undefined,
  ];

  const fhir: fhir4.Practitioner = {
    ...mapPersonContactToFhirPartial(contact),
    ...fhirGeneral(general),
    resourceType: 'Practitioner',
    qualification: qualificationCode,
    active,
    extension: compact(extensionList),
  };

  const registrationNumber = createOptionalIdentifier(
    IdentifierCode.registrationNumber,
    practitioner.qualification.registrationNumber,
  );
  const ampathMnemonic = createOptionalIdentifier(
    IdentifierCode.ampathMnemonic,
    practitioner.external?.ampathMnemonic,
  );

  const withRegisterNumber = attachIdentifiers(
    fhir,
    registrationNumber,
    ampathMnemonic,
  );

  return withRegisterNumber;
};

// TODO: Update qualification to a flat model
export const mapFhirToModelPractitionerWithId = (
  fhir: WithId<fhir4.Practitioner>,
): PractitionerCreateResponse => {
  const practitioner = mapFhirToModelPractitioner(fhir);

  return {
    ...practitioner,
    id: fhir.id,
  };
};

export const mapFhirToModelOnePractitionerWithId = (
  fhir: WithId<fhir4.Practitioner>,
  groups: CreateReference[],
  organizations: CreateReference[],
  assignedPatients: CreateReference[],
): PractitionerGetOneResponse => {
  const practitioner = mapFhirToModelPractitioner(fhir);

  return {
    ...practitioner,
    id: fhir.id,
    groups,
    organizations,
    assignedPatients,
  };
};

export const mapUpdateGeneralModelToFhirPractitioner = (
  fhir: WithId<fhir4.Practitioner>,
  form: Partial<PractitionerGeneral>,
): WithId<fhir4.Practitioner> => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);

  const { contact, qualification, active, external, settings } =
    modelPractitioner;

  const fhirPractitioner = mapModelToFhirPractitioner({
    contact,
    general: form,
    qualification,
    external,
    active,
    settings,
  });

  return { ...fhirPractitioner, id: fhir.id };
};

export const mapUpdateContactModelToFhirPractitioner = (
  fhir: WithId<fhir4.Practitioner>,
  form: PersonContact,
): WithId<fhir4.Practitioner> => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);

  const { general, qualification, active, external, settings } =
    modelPractitioner;

  const fhirPractitioner = mapModelToFhirPractitioner({
    contact: form,
    general,
    qualification,
    external,
    active,
    settings,
  });

  return { ...fhirPractitioner, id: fhir.id };
};

export const mapUpdateQualificationModelToFhirPractitioner = (
  fhir: WithId<fhir4.Practitioner>,
  form: QualificationFields,
): WithId<fhir4.Practitioner> => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);

  const { contact, general, active, external, settings } = modelPractitioner;

  const fhirPractitioner = mapModelToFhirPractitioner({
    contact,
    general,
    qualification: form,
    external,
    active,
    settings,
  });

  return { ...fhirPractitioner, id: fhir.id };
};

export const updatePractitionerExternal = (
  fhir: WithId<fhir4.Practitioner>,
  form: External,
): WithId<fhir4.Practitioner> => {
  const modelPractitioner = mapFhirToModelPractitioner(fhir);

  const fhirPractitioner = mapModelToFhirPractitioner({
    ...modelPractitioner,
    external: form,
  });

  return { ...fhirPractitioner, id: fhir.id };
};

export const mapUpdatePractitionerSettings = (
  fhir: WithId<fhir4.Practitioner>,
  form: Settings,
): WithId<fhir4.Practitioner> => {
  const { canPrescribe } = form;

  const canPrescribeExtension =
    canPrescribe !== undefined
      ? createCanPrescribeExtension(canPrescribe)
      : undefined;

  const extension = updateExtensions(
    ExtensionUrl.medicationRequestPrescriber,
    fhir.extension,
    compact([canPrescribeExtension]),
  );

  return { ...fhir, extension };
};
